import { useState } from "react";
import { useAccountDefaultTemplate } from "@/lib/account-default-template";
import {
  type Examination,
  pickRecorderTemplate,
  toRecorderFormData,
} from "@/lib/recorder-note-settings";
import {
  readFavorites,
  splitFavorites,
  type TemplateChoice,
  templateChoices,
  useTemplatePreferenceVersion,
} from "@/lib/template-preferences";
import { useOptionalTemplateList } from "@/lib/template-queries";
import type { RecorderFormSchemaType } from "@/schemas/newVisit.schema";
import { useAuthStore } from "@/store/auth.store";

export interface RecorderNoteSettings {
  // The template list has loaded or failed (then `values` is null).
  ready: boolean;
  template: string;
  templateName: string;
  examination: Examination;
  favorites: TemplateChoice[];
  builtIn: TemplateChoice[];
  custom: TemplateChoice[];
  setTemplate: (value: string) => void;
  setExamination: (value: Examination) => void;
  // What Stop submits; null until ready, or when no template is usable.
  values: RecorderFormSchemaType | null;
}

// Note settings for a v3.2 recording (template chip on the recorder; Stop
// submits them without the classic sheet). See lib/recorder-note-settings.
export const useRecorderNoteSettings = (
  preferredTemplate?: string,
  enabled = true,
): RecorderNoteSettings => {
  const userData = useAuthStore((state) => state.userData);
  const { templates, loading, error } = useOptionalTemplateList(enabled);
  const defaultTemplate = useAccountDefaultTemplate(enabled);
  useTemplatePreferenceVersion();
  const [chosen, setTemplate] = useState<string | null>(null);
  const [examination, setExamination] = useState<Examination>("Physical");
  const account = userData?.public_id;
  const template = pickRecorderTemplate({
    chosen,
    preferred: preferredTemplate,
    fallback: defaultTemplate.value,
    templates,
  });
  const choices = templateChoices(templates);
  const split = splitFavorites(choices, readFavorites(account));
  // A failed template list still counts as ready: `values` is then null and
  // the classic sheet (with its own Retry) is shown instead.
  const ready = enabled && !loading && !defaultTemplate.loading;
  return {
    ready,
    template,
    templateName:
      choices.find((choice) => choice.value === template)?.name ?? template,
    examination,
    favorites: split.favorites,
    builtIn: split.others.filter((choice) => choice.builtIn),
    custom: split.others.filter((choice) => !choice.builtIn),
    setTemplate,
    setExamination,
    values:
      ready && !error
        ? toRecorderFormData({
            template,
            examination,
            templates,
            style: userData?.style,
            format: userData?.format,
          })
        : null,
  };
};
